import { z } from 'zod';

import {
  CurrencyCodeSchema,
  EmailSchema,
  IsoDateTimeSchema,
  PaiseSchema,
  UuidSchema,
} from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Payment shapes (Phase 6 - PayU hosted checkout + refunds).
 *
 *   - PaymentListItem / Detail / EventEntry - read models
 *   - InitiatePayment            - returns the PayU form fields the client
 *                                  POSTs to PayU's hosted checkout page
 *   - PayUWebhookPayload         - shape PayU posts back to our callback URL
 *   - InitiateRefund / Approve   - two-person refund workflow (FINANCE
 *                                  initiates, ADMIN approves above threshold)
 *
 * PCI scope: SAQ-A only. Card data NEVER touches our servers. We post the
 * customer to PayU's hosted checkout; PayU posts back to our success/failure
 * URL and (separately) to our webhook with a SHA-512 signed payload.
 */

// ---------------------------------------------------------------------------
// Enums (mirror prisma/schema.prisma)
// ---------------------------------------------------------------------------

export const PaymentProviderSchema = z.enum(['PAYU', 'RAZORPAY']);
export type PaymentProvider = z.infer<typeof PaymentProviderSchema>;

export const PaymentStatusSchema = z.enum([
  'PENDING',
  'INITIATED',
  'SUCCESS',
  'FAILED',
  'REFUND_PENDING',
  'REFUNDED',
]);
export type PaymentStatus = z.infer<typeof PaymentStatusSchema>;

export const PaymentEventTypeSchema = z.enum([
  'INITIATED',
  'WEBHOOK_RECEIVED',
  'SUCCESS_CALLBACK',
  'FAILURE_CALLBACK',
  'REFUND_INITIATED',
  'REFUND_CALLBACK',
  'SIGNATURE_VERIFICATION_FAILED',
]);
export type PaymentEventType = z.infer<typeof PaymentEventTypeSchema>;

// ---------------------------------------------------------------------------
// Reference sub-schemas
// ---------------------------------------------------------------------------

const UserRefSchema = z.object({ id: UuidSchema, fullName: z.string() });
const BookingRefSchema = z.object({
  id: UuidSchema,
  bookingNumber: z.string(),
});
const CustomerRefSchema = z.object({
  id: UuidSchema,
  displayName: z.string(),
});

// ---------------------------------------------------------------------------
// Payment events (audit log)
// ---------------------------------------------------------------------------

export const PaymentEventSchema = z.object({
  id: UuidSchema,
  eventType: PaymentEventTypeSchema,
  signatureValid: z.boolean().nullable(),
  payload: z.record(z.string(), z.unknown()).nullable(),
  sourceIp: z.string().nullable(),
  userAgent: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
});
export type PaymentEvent = z.infer<typeof PaymentEventSchema>;

// ---------------------------------------------------------------------------
// Payment list + detail
// ---------------------------------------------------------------------------

export const PaymentListItemSchema = z.object({
  id: UuidSchema,
  paymentNumber: z.string(),
  booking: BookingRefSchema,
  customer: CustomerRefSchema,
  provider: PaymentProviderSchema,
  status: PaymentStatusSchema,
  amountPaise: PaiseSchema,
  refundedPaise: PaiseSchema,
  currency: z.object({ id: UuidSchema, code: CurrencyCodeSchema }).nullable(),
  paymentMethod: z.string().nullable(),
  initiatedAt: IsoDateTimeSchema.nullable(),
  completedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
});
export type PaymentListItem = z.infer<typeof PaymentListItemSchema>;

export const PaymentListQuerySchema = PaginationQuerySchema.extend({
  bookingId: UuidSchema.optional(),
  customerId: UuidSchema.optional(),
  status: PaymentStatusSchema.optional(),
  provider: PaymentProviderSchema.optional(),
  createdInLastDays: z.coerce.number().int().min(1).max(365).optional(),
});
export type PaymentListQuery = z.infer<typeof PaymentListQuerySchema>;

export const PaymentListResponseSchema = paginatedResponseSchema(PaymentListItemSchema);
export type PaymentListResponse = z.infer<typeof PaymentListResponseSchema>;

export const PaymentDetailSchema = PaymentListItemSchema.extend({
  providerOrderId: z.string().nullable(),
  providerPaymentId: z.string().nullable(),
  idempotencyKey: z.string().nullable(),
  failedAt: IsoDateTimeSchema.nullable(),
  failureReason: z.string().nullable(),
  refundInitiator: UserRefSchema.nullable(),
  refundApprover: UserRefSchema.nullable(),
  refundRequestedAt: IsoDateTimeSchema.nullable(),
  refundApprovedAt: IsoDateTimeSchema.nullable(),
  refundCompletedAt: IsoDateTimeSchema.nullable(),
  refundReason: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  events: z.array(PaymentEventSchema),
  updatedAt: IsoDateTimeSchema,
});
export type PaymentDetail = z.infer<typeof PaymentDetailSchema>;

// ---------------------------------------------------------------------------
// Initiate payment (sales agent / customer flow)
// ---------------------------------------------------------------------------

export const InitiatePaymentRequestSchema = z.object({
  bookingId: UuidSchema,
  /** Partial-pay supported - amount may be less than booking grand total. */
  amountPaise: PaiseSchema.refine((v) => v > 0, 'amountPaise must be > 0'),
  /** Optional. If omitted, server-generates a fresh UUID. Clients re-send
   *  the same key on retry to dedupe a double-charge. */
  idempotencyKey: z.string().min(8).max(120).optional(),
  /** Customer details PayU requires on its hosted form. Pre-filled from
   *  the booking's customer record where available. */
  customerEmail: EmailSchema,
  customerPhone: z.string().min(8).max(20),
  customerFirstName: z.string().min(1).max(120),
  /** Free-form line describing what the payment is for, surfaced to the
   *  customer on PayU's hosted page. Capped at PayU's 100-char limit. */
  productInfo: z.string().min(2).max(100),
});
export type InitiatePaymentRequest = z.infer<typeof InitiatePaymentRequestSchema>;

/**
 * What the API returns from /payments/initiate. The client renders a form
 * with these fields and POSTs to `payuActionUrl`. PayU then redirects the
 * customer back to surl/furl with the result.
 */
export const InitiatePaymentResponseSchema = z.object({
  paymentId: UuidSchema,
  paymentNumber: z.string(),
  payuActionUrl: z.string().url(),
  /** Form fields to render as hidden inputs on the client-side form. */
  formFields: z.object({
    key: z.string(),
    txnid: z.string(),
    amount: z.string(), // PayU wants 2-decimal string (e.g. "1234.56")
    productinfo: z.string(),
    firstname: z.string(),
    email: EmailSchema,
    phone: z.string(),
    surl: z.string().url(),
    furl: z.string().url(),
    hash: z.string(), // SHA-512 of key|txnid|amount|productinfo|firstname|email|||||||||||salt
    udf1: z.string().optional(),
    udf2: z.string().optional(),
    udf3: z.string().optional(),
    udf4: z.string().optional(),
    udf5: z.string().optional(),
  }),
});
export type InitiatePaymentResponse = z.infer<typeof InitiatePaymentResponseSchema>;

// ---------------------------------------------------------------------------
// PayU webhook payload (server-to-server callback)
// ---------------------------------------------------------------------------

/**
 * What PayU posts to our webhook endpoint. PayU sends `application/x-www-form-urlencoded`,
 * so all values arrive as strings. Hash verification is performed before
 * touching any field; if signature is bad, we still log the body to
 * payment_events as SIGNATURE_VERIFICATION_FAILED and return 200 so PayU
 * stops retrying (we'll investigate manually).
 */
export const PayUWebhookPayloadSchema = z
  .object({
    mihpayid: z.string().describe("PayU's own transaction id"),
    /** Our outgoing txnid - resolves to payments.provider_order_id. */
    txnid: z.string(),
    /** Amount as a 2-decimal string (e.g. "1234.56"). */
    amount: z.string(),
    productinfo: z.string(),
    firstname: z.string(),
    email: z.string(),
    status: z.enum(['success', 'failure', 'pending', 'usercancel']),
    /** PayU's SHA-512 hash for verification. */
    hash: z.string(),
    /** Mode of payment: CC / DC / NB / UPI / WALLET / EMI etc. */
    mode: z.string().optional(),
    /** Bank reference number on success. */
    bank_ref_num: z.string().optional(),
    /** Failure / cancel reason. */
    error: z.string().optional(),
    error_Message: z.string().optional(),
    unmappedstatus: z.string().optional(),
    /** User-defined fields we round-tripped. */
    udf1: z.string().optional(),
    udf2: z.string().optional(),
    udf3: z.string().optional(),
    udf4: z.string().optional(),
    udf5: z.string().optional(),
  })
  .catchall(z.string()); // PayU adds new fields without notice - keep them as Json metadata
export type PayUWebhookPayload = z.infer<typeof PayUWebhookPayloadSchema>;

// ---------------------------------------------------------------------------
// Refund flow (two-person rule)
// ---------------------------------------------------------------------------

export const InitiateRefundRequestSchema = z.object({
  /** Refund amount in paise. Must be <= (amountPaise - refundedPaise). */
  amountPaise: PaiseSchema.refine((v) => v > 0, 'Refund amount must be > 0'),
  reason: z.string().min(3).max(2000),
});
export type InitiateRefundRequest = z.infer<typeof InitiateRefundRequestSchema>;

export const ApproveRefundRequestSchema = z.object({
  /** Approver may adjust the amount down (never up). */
  amountPaise: PaiseSchema.optional(),
  approvalNote: z.string().max(2000).nullable().optional(),
});
export type ApproveRefundRequest = z.infer<typeof ApproveRefundRequestSchema>;
